/**
 * Conferir envio (E144.F4, GUS-105 + GUS-135, ADR-0098 §2.2–2.4): as CCBs que o envio identificou,
 * uma por linha, com a pendência curta de cada uma; a pergunta localizada do arquivo de apoio sem
 * destino; o aviso de biometria não apresentada; a custódia curta com o recibo sob pedido. "Concluir
 * envio" desdobra o envio — um caso por CCB — e leva ao recibo dos casos.
 *
 * Enquanto os arquivos são lidos, a tela relê sozinha e diz "Verificação em andamento"; concluir
 * espera a leitura e as respostas. O preenchimento do cadastro segue na pré-análise de cada caso.
 *
 * E162.F2.T04: a CCB que já está em outro caso abre esse caso daqui.
 *
 * E165.F1.T04/T05 (GUS-135, GUS-105, GUS-202, HTML 6.8): a tabela CCB / Exame / Destino (o caso que
 * já tem a CCB se abre do destino), "Ver arquivos e identificação" recolhido e a "Conferência do
 * CPF"; o CPF divergente trava "Concluir envio" e leva de volta ao Enviar. A custódia sai por inteiro
 * do Conferir (ADR-0114 §3): o recibo fica no Enviar e no caso. O aviso de biometria não apresentada
 * saiu com as explicações (GUS-135): a limitação segue com a equipe e no laudo.
 *
 * E165.F1.T06 (GUS-105, ADR-0114 §2): a CCB que já tem caso pede a escolha explícita — vincular ou
 * abrir novo; no fechado, a nova análise —, e "Concluir envio" espera todas. A recusa do servidor
 * por escolha (a situação mudou desde a leitura) relê o envio para mostrar a pergunta.
 *
 * E177.F5 (GUS-253, ADR-0130; HTML 6.8 por CCB): com CCB declarada, a linha diz "CCB 4201" e as
 * contagens, os documentos vêm agrupados por CCB ("Editar documentos e vínculos" volta aos contratos)
 * e a CCB com o identificador do pacote divergente pede corrigir ou manter antes de concluir. A
 * recusa por CCB (sem arquivo, divergente) diz o porquê e relê o envio.
 *
 * E189.F2 (ADR-0138): com a leitura em curso, o botão é "Enviar e seguir" — o pedido fica gravado, o
 * sistema conclui quando a leitura terminar e a pessoa segue (`onEnviado`). O caso cujo pedido parou
 * por uma decisão mostra "Precisa da sua conferência" com o motivo (`parada`), e concluir é o de sempre.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { AberturaDoCasoData, EscolhaDoContrato } from '@/lib/abertura_do_caso';
import { pedirConclusao, type MotivoDaParada } from '@/lib/cadastro_sem_espera';
import {
  concluirAbertura,
  ConclusaoRecusadaError,
  type ConclusaoDaAberturaData,
} from '@/lib/conclusao_da_abertura';

import { ConferenciaDoCpf } from './ConferenciaDoCpf';
import { divergentesSemDecisao } from './conferenciaPorCcb';
import { comOsDeclarados, semDestinoSolto } from './contratosDeclarados';
import { ContratosDoEnvio } from './ContratosDoEnvio';
import { DocumentosPorCcb } from './DocumentosPorCcb';
import { escolhasParaConcluir, escolhasPendentes, type EscolhasDoEnvio } from './escolhaDaCcb';
import { mensagemDaIdentificacao } from './faltasDaIdentificacao';
import { AvisoDaAbertura, BOTAO_DO_RODAPE, RODAPE_DA_JORNADA } from './PecasDaAbertura';
import { PerguntasDoApoio } from './PerguntasDoApoio';
import { ConflitosDasCcbs } from './PorCcbNoConferir';
import { useAberturaDoCaso } from './useAberturaDoCaso';

const t = ptBR.abertura.conferirEnvio;
const tc = ptBR.abertura.conferirPorCcb;
const tcad = ptBR.abertura.cadastro;

const ERRO_POR_MOTIVO: Record<ConclusaoRecusadaError['motivo'], string> = {
  pergunta: t.perguntaPendente,
  em_leitura: t.erroLendo,
  sem_arquivo: t.erroSemArquivo,
  identificacao: t.erroIdentificacao,
  cpf_divergente: t.erroCpfDivergente,
  escolha: t.erroEscolha,
  ccb_sem_arquivo: tc.erroSemArquivo,
  ccb_divergente: tc.erroDivergente,
  servidor: ptBR.abertura.falhaDaConclusao.servidor,
  rede: ptBR.abertura.falhaDaConclusao.rede,
  outro: t.erro,
};

/** A recusa que a releitura do envio explica: a situação ou a divergência mudou desde a leitura. */
const RELE_O_ENVIO: ReadonlySet<ConclusaoRecusadaError['motivo']> = new Set([
  'escolha',
  'ccb_divergente',
]);

export interface ConferirEnvioProps {
  client: AxiosInstance;
  caseId: string;
  onVoltar: () => void;
  onConcluido: (conclusao: ConclusaoDaAberturaData) => void;
  /** E162.F2.T04: o caso que já tem a CCB, pela rota do papel (portal ou equipe). */
  onAbrirCaso: (caseId: string) => void;
  /**
   * F-E163.F3-01: as respostas sobre os apoios guardadas pela jornada, que sobrevivem à consulta da
   * custódia (ela desmonta este passo). Sem elas, o passo guarda as respostas sozinho.
   */
  respostas?: Record<string, string>;
  onRespostas?: (respostas: Record<string, string>) => void;
  /** E165.F1.T06: as escolhas das CCBs que já têm caso, guardadas pela jornada como as respostas. */
  escolhas?: EscolhasDoEnvio;
  onEscolhas?: (escolhas: EscolhasDoEnvio) => void;
  /** E189.F2: o pedido de conclusão que parou numa decisão, com o motivo. */
  parada?: MotivoDaParada | null;
  /** E189.F2: o "Enviar e seguir" gravou o pedido; a página leva a pessoa adiante. */
  onEnviado?: (codigo: string) => void;
}

function pendentes(dados: AberturaDoCasoData, respostas: Record<string, string>): number {
  return (dados.envio?.perguntas ?? []).filter(
    (p) => respostas[p.unidade.documento_id] === undefined,
  ).length;
}

function useGuardado<V extends string>(
  guardado: Record<string, V> | undefined,
  onGuardar: ((valores: Record<string, V>) => void) | undefined,
): [Record<string, V>, (valores: Record<string, V>) => void] {
  const [proprios, setProprios] = useState<Record<string, V>>({});
  return [guardado ?? proprios, onGuardar ?? setProprios];
}

/**
 * "Concluir envio" espera a leitura, o CPF que não diverge, a escolha de cada CCB com caso e ao menos
 * um contrato examinável (E133.F2.C01: o envio só com arquivo guardado sem exame não conclui).
 */
function concluirTravado(dados: AberturaDoCasoData, escolhas: EscolhasDoEnvio): boolean {
  const envio = dados.envio;
  const verificando = (envio?.em_verificacao ?? false) || dados.composicao.em_leitura;
  const contratos = envio?.contratos ?? [];
  const pendentes = escolhasPendentes(contratos, escolhas);
  return (
    verificando ||
    contratos.length === 0 ||
    dados.conferencia_do_cpf === 'divergente' ||
    pendentes.length > 0 ||
    divergentesSemDecisao(contratos).length > 0
  );
}

function CarregandoOConferir({ erro }: { erro: boolean }): JSX.Element {
  return erro ? (
    <p role="alert" className="text-body-sm text-danger" data-testid="conferir-erro">
      {t.erroAoCarregar}
    </p>
  ) : (
    <p className="text-body-sm text-fg-muted">{t.carregando}</p>
  );
}

export function ConferirEnvio(props: ConferirEnvioProps): JSX.Element {
  const { client, caseId } = props;
  const { dados, erro, recarregar } = useAberturaDoCaso(client, caseId, { releitura: true });
  const [respostas, setRespostas] = useGuardado(props.respostas, props.onRespostas);
  const [escolhas, setEscolhas] = useGuardado<EscolhaDoContrato>(props.escolhas, props.onEscolhas);
  const [tentou, setTentou] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const [falha, setFalha] = useState<string | null>(null);

  if (dados === null) return <CarregandoOConferir erro={erro} />;
  const envio = dados.envio ?? { contratos: [], perguntas: [], em_verificacao: false };
  const verificando = envio.em_verificacao || dados.composicao.em_leitura;
  const contratos = comOsDeclarados(envio, respostas);

  async function concluir(): Promise<void> {
    if (dados === null) return;
    setTentou(true);
    setFalha(null);
    if (pendentes(dados, respostas) > 0) return;
    setConcluindo(true);
    try {
      const validas = escolhasParaConcluir(dados.envio?.contratos ?? [], escolhas);
      props.onConcluido(await concluirAbertura(client, caseId, respostas, validas));
    } catch (err) {
      const motivo = err instanceof ConclusaoRecusadaError ? err.motivo : 'outro';
      setFalha(
        motivo === 'identificacao' && err instanceof ConclusaoRecusadaError
          ? mensagemDaIdentificacao(err.unidades)
          : ERRO_POR_MOTIVO[motivo],
      );
      setConcluindo(false);
      if (RELE_O_ENVIO.has(motivo)) await recarregar();
    }
  }

  async function enviarESeguir(): Promise<void> {
    if (dados === null) return;
    setFalha(null);
    setConcluindo(true);
    try {
      const validas = escolhasParaConcluir(dados.envio?.contratos ?? [], escolhas);
      const resposta = await pedirConclusao(client, caseId, respostas, validas);
      if (resposta.estado === 'concluido') props.onConcluido(resposta.conclusao);
      else props.onEnviado?.(dados.codigo);
    } catch (err) {
      const motivo = err instanceof ConclusaoRecusadaError ? err.motivo : 'outro';
      setFalha(motivo === 'outro' ? tcad.erroAoEnviar : ERRO_POR_MOTIVO[motivo]);
      setConcluindo(false);
    }
  }

  return (
    <div
      className="flex flex-col gap-5"
      data-testid="abertura-passo-conferir"
      aria-busy={verificando}
    >
      <AvisosDoConferir parada={props.parada ?? null} verificando={verificando} />
      {!verificando && envio.contratos.length === 0 ? (
        <AvisoDaAbertura
          tom="aviso"
          titulo={t.semExaminavelTitulo}
          texto={t.semExaminavelTexto}
          testId="conferir-sem-examinavel"
        />
      ) : null}
      <ContratosDoEnvio
        contratos={contratos}
        arquivos={dados.recibo.originais.map((o) => o.nome)}
        pasta={dados.pasta}
        processo={dados.process_number}
        cpf={dados.cpf_informado_mascarado ?? null}
        onAbrirCaso={props.onAbrirCaso}
        escolhas={escolhas}
        onEscolher={(chave, escolha) => setEscolhas({ ...escolhas, [chave]: escolha })}
        disabled={concluindo}
        porCcb={
          <>
            <ConflitosDasCcbs
              client={client}
              caseId={caseId}
              contratos={contratos}
              onCorrigir={props.onVoltar}
              onMantida={recarregar}
              disabled={concluindo}
            />
            <DocumentosPorCcb
              client={client}
              caseId={caseId}
              contratos={contratos}
              onEditar={props.onVoltar}
            />
          </>
        }
      />
      <PerguntasDoApoio
        perguntas={envio.perguntas}
        contratos={contratos}
        respostas={respostas}
        onResponder={(documentoId, resposta) =>
          setRespostas(semDestinoSolto(envio, { ...respostas, [documentoId]: resposta }))
        }
        apontarPendentes={tentou}
        disabled={concluindo}
      />
      {verificando ? null : (
        <ConferenciaDoCpf estado={dados.conferencia_do_cpf} onCorrigir={props.onVoltar} />
      )}
      {falha !== null ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="conferir-falha">
          {falha}
        </p>
      ) : null}
      <RodapeDoConferir
        concluindo={concluindo}
        travado={concluirTravado(dados, escolhas)}
        emLeitura={verificando}
        onVoltar={props.onVoltar}
        onConcluir={() => void concluir()}
        onEnviarESeguir={() => void enviarESeguir()}
      />
    </div>
  );
}

/** E189.F2: a parada do pedido com o motivo e a leitura em curso com o "Enviar e seguir". */
function AvisosDoConferir({
  parada,
  verificando,
}: {
  parada: MotivoDaParada | null;
  verificando: boolean;
}): JSX.Element {
  return (
    <>
      {parada !== null ? (
        <AvisoDaAbertura
          tom="aviso"
          titulo={tcad.precisaTitulo}
          texto={ERRO_POR_MOTIVO[parada]}
          testId="conferir-parada"
        />
      ) : null}
      {verificando ? (
        <AvisoDaAbertura
          tom="aviso"
          titulo={t.verificando}
          texto={`${t.verificandoTexto} ${tcad.enviarESeguirAjuda}`}
          testId="conferir-verificando"
        />
      ) : null}
    </>
  );
}

function RodapeDoConferir(props: {
  concluindo: boolean;
  travado: boolean;
  /** E189.F2: com a leitura em curso, a ação é "Enviar e seguir". */
  emLeitura: boolean;
  onVoltar: () => void;
  onConcluir: () => void;
  onEnviarESeguir: () => void;
}): JSX.Element {
  return (
    <div className={RODAPE_DA_JORNADA}>
      <Button
        type="button"
        variant="secondary"
        className={BOTAO_DO_RODAPE}
        data-testid="abertura-voltar"
        onClick={props.onVoltar}
        disabled={props.concluindo}
      >
        {ptBR.abertura.voltar}
      </Button>
      {props.emLeitura ? (
        <Button
          type="button"
          className={BOTAO_DO_RODAPE}
          data-testid="abertura-enviar-e-seguir"
          disabled={props.concluindo}
          onClick={props.onEnviarESeguir}
        >
          {props.concluindo ? tcad.enviando : tcad.enviarESeguir}
        </Button>
      ) : (
        <Button
          type="button"
          className={BOTAO_DO_RODAPE}
          data-testid="abertura-concluir-envio"
          disabled={props.travado || props.concluindo}
          onClick={props.onConcluir}
        >
          {props.concluindo ? t.concluindo : t.concluir}
        </Button>
      )}
    </div>
  );
}
